import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import Sidebar from "./Navbar";
import { BASE_URL } from "./constants";

const Bookings = () => {
  const navigate = useNavigate();
  const admin = JSON.parse(localStorage.getItem("admin"));
  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [limit] = useState(15);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin");
    navigate("/login");
  };

  const fetchBookings = async (page = 1, status = statusFilter) => {
    setLoading(true);
    setError("");
    try {
      const statusParam = status ? `&status=${status}` : "";
      const response = await fetch(
        `${BASE_URL}admin/bookings?limit=${limit}&offset=${(page - 1) * limit}${statusParam}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      const data = await response.json();
      if (data.status === "success") {
        setBookings(data.data.bookings || []);
        setSummary(data.data.summary || null);
        setTotalBookings(data.data.pagination?.total || 0);
        setTotalPages(
          Math.max(1, Math.ceil((data.data.pagination?.total || 0) / limit)),
        );
        setCurrentPage(page);
      } else {
        setError(data.message || "Failed to fetch bookings");
      }
    } catch (err) {
      setError("Error connecting to bookings service");
      console.error("Bookings fetch error:", err);
    }
    setLoading(false);
  };

  const fetchBookingDetails = async (bookingId) => {
    setActionLoading(true);
    setError("");
    try {
      const response = await fetch(`${BASE_URL}admin/bookings/${bookingId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      if (data.status === "success") {
        setSelectedBooking(data.data);
        setShowDetailsModal(true);
      } else {
        setError(data.message || "Failed to fetch booking details");
      }
    } catch (err) {
      setError("Error fetching booking details");
      console.error("Booking details fetch error:", err);
    }
    setActionLoading(false);
  };

  const cancelBooking = async (bookingId, reason) => {
    setActionLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${BASE_URL}admin/bookings/${bookingId}/cancel`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ reason }),
        },
      );
      const data = await response.json();
      if (data.status === "success") {
        setShowCancelModal(false);
        setCancelReason("");
        setSuccess("Booking cancelled successfully.");
        setTimeout(() => setSuccess(""), 4000);
        fetchBookings(currentPage, statusFilter);
      } else {
        setError(data.message || "Failed to cancel booking");
      }
    } catch (err) {
      setError("Error cancelling booking");
      console.error("Cancel booking error:", err);
    }
    setActionLoading(false);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    fetchBookings(1, status);
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // Filter current page bookings by search query (Client, Provider, Service, RoomID)
  const filteredBookings = useMemo(() => {
    if (!searchQuery.trim()) return bookings;
    const q = searchQuery.toLowerCase().trim();
    return bookings.filter((b) => {
      const matchRoom = b.roomId?.toLowerCase().includes(q);
      const matchService = b.service?.name?.toLowerCase().includes(q);
      const matchSubService = b.subService?.name?.toLowerCase().includes(q);
      const matchUser =
        b.user?.name?.toLowerCase().includes(q) || b.user?.phone?.includes(q);
      const matchProvider =
        b.provider?.name?.toLowerCase().includes(q) ||
        b.provider?.phone?.includes(q);
      return (
        matchRoom ||
        matchService ||
        matchSubService ||
        matchUser ||
        matchProvider
      );
    });
  }, [bookings, searchQuery]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "COMPLETED":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "PENDING":
        return "bg-amber-50 text-amber-800 border-amber-200";
      case "ACCEPTED":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "STARTED":
      case "ONGOING":
        return "bg-purple-50 text-purple-700 border-purple-200";
      case "CANCELLED":
        return "bg-rose-50 text-rose-700 border-rose-200";
      case "SEARCHING":
        return "bg-amber-100 text-amber-900 border-amber-300 font-bold";
      case "REJECTED":
        return "bg-stone-100 text-stone-700 border-stone-200";
      default:
        return "bg-stone-100 text-stone-600 border-stone-200";
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      <Sidebar admin={admin} onLogout={handleLogout} />

      {/* Main Content Area */}
      <main className="flex-1 lg:ml-72 min-h-screen flex flex-col">
        {/* Sticky Executive Topbar */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-stone-200/80 px-4 sm:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold text-stone-400">
                <span>Admin</span>
                <span>/</span>
                <span className="text-stone-700">Orders & Reservations</span>
              </div>
              <h1 className="text-xl font-extrabold text-stone-900 tracking-tight flex items-center gap-2">
                <span>Bookings Registry</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#F8D346]/20 text-stone-900 border border-[#F8D346]/40">
                  {totalBookings} Total Reservations
                </span>
              </h1>
            </div>

            <div className="flex items-center gap-2.5">
              <button
                onClick={() => fetchBookings(currentPage, statusFilter)}
                className="p-2 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 transition-colors shadow-xs"
                title="Refresh Bookings"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            </div>
          </div>
        </header>

        {/* Page Body Container */}
        <div className="p-4 sm:p-8 max-w-7xl w-full mx-auto space-y-6">
          {/* Notifications */}
          {success && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold shadow-sm flex items-center gap-2">
              <svg
                className="w-5 h-5 text-emerald-600 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{success}</span>
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm font-semibold shadow-sm flex items-center gap-2">
              <svg
                className="w-5 h-5 text-rose-600 shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Section 1: Real Summary Metrics */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {[
                {
                  key: "ALL",
                  label: "All Orders",
                  val: totalBookings,
                  filter: "",
                  color: "border-stone-200/80 bg-white",
                },
                {
                  key: "COMPLETED",
                  label: "Completed",
                  val: summary.COMPLETED || 0,
                  filter: "COMPLETED",
                  color: "border-emerald-200/80 bg-emerald-50/50",
                },
                {
                  key: "PENDING",
                  label: "Pending",
                  val: summary.PENDING || 0,
                  filter: "PENDING",
                  color: "border-amber-200/80 bg-amber-50/50",
                },
                {
                  key: "ACCEPTED",
                  label: "Accepted",
                  val: summary.ACCEPTED || 0,
                  filter: "ACCEPTED",
                  color: "border-blue-200/80 bg-blue-50/50",
                },
                {
                  key: "ONGOING",
                  label: "In Progress",
                  val: (summary.ONGOING || 0) + (summary.STARTED || 0),
                  filter: "ONGOING",
                  color: "border-purple-200/80 bg-purple-50/50",
                },
                {
                  key: "SEARCHING",
                  label: "Searching",
                  val: summary.SEARCHING || 0,
                  filter: "SEARCHING",
                  color: "border-amber-300/80 bg-amber-100/40",
                },
                {
                  key: "CANCELLED",
                  label: "Cancelled",
                  val: summary.CANCELLED || 0,
                  filter: "CANCELLED",
                  color: "border-rose-200/80 bg-rose-50/50",
                },
                {
                  key: "REJECTED",
                  label: "Rejected",
                  val: summary.REJECTED || 0,
                  filter: "REJECTED",
                  color: "border-stone-200/80 bg-stone-50",
                },
              ].map((card) => {
                const isActive = statusFilter === card.filter;
                return (
                  <button
                    key={card.key}
                    onClick={() => handleStatusFilterChange(card.filter)}
                    className={`p-3.5 rounded-2xl border text-left transition-all ${
                      card.color
                    } ${
                      isActive
                        ? "ring-2 ring-stone-900 shadow-sm"
                        : "hover:border-stone-400"
                    }`}
                  >
                    <div className="text-xl font-black text-stone-900">
                      {card.val}
                    </div>
                    <div className="text-[11px] font-bold text-stone-500 uppercase tracking-wider mt-0.5">
                      {card.label}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Section 2: Search Bar & Segmented Filter */}
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
              {/* Search Bar */}
              <div className="relative flex-1">
                <svg
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search by Booking Room ID, Client Name/Phone, Provider, or Service..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-stone-50 hover:bg-stone-100/60 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-[#F8D346] transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-xs font-bold"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Status Selector Dropdown */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-stone-400 whitespace-nowrap">
                  Filter:
                </span>
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  className="px-3.5 py-2.5 bg-stone-50 focus:bg-white border border-stone-200 rounded-xl text-xs font-bold text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#F8D346]"
                >
                  <option value="">All Statuses ({totalBookings})</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="ACCEPTED">Accepted</option>
                  <option value="PENDING">Pending</option>
                  <option value="ONGOING">Ongoing / In-Progress</option>
                  <option value="SEARCHING">Searching / Unmatched</option>
                  <option value="STARTED">Started</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 3: Bookings Data Table */}
          <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
              <div>
                <h3 className="font-black text-stone-900 text-base flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#F8D346]"></span>
                  Reservations Table
                </h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  Real-time service orders and dispatches
                </p>
              </div>
              <span className="text-xs font-bold text-stone-500 bg-stone-100 px-3 py-1 rounded-full">
                Showing {filteredBookings.length} on page {currentPage}
              </span>
            </div>

            {loading ? (
              <div className="p-16 text-center">
                <div className="w-10 h-10 border-4 border-[#F8D346] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-sm font-bold text-stone-700">
                  Loading bookings register...
                </p>
                <p className="text-xs text-stone-400 mt-1">
                  Querying database records
                </p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="p-16 text-center">
                <div className="w-16 h-16 bg-stone-100 text-stone-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                <h4 className="text-base font-bold text-stone-800 mb-1">
                  No Bookings Found
                </h4>
                <p className="text-xs text-stone-400">
                  {searchQuery
                    ? `No reservations match "${searchQuery}".`
                    : "No bookings found for the selected status."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-stone-50/70 border-b border-stone-100 text-[11px] font-extrabold uppercase tracking-wider text-stone-400">
                      <th className="py-3 px-4">Booking ID</th>
                      <th className="py-3 px-4">Service & Trade</th>
                      <th className="py-3 px-4">User / Client</th>
                      <th className="py-3 px-4">Buddy / Provider</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Scheduled Slot</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 text-xs">
                    {filteredBookings.map((b) => (
                      <tr
                        key={b.id}
                        className="hover:bg-stone-50/70 transition-colors"
                      >
                        {/* Booking ID */}
                        <td className="py-3.5 px-4">
                          <span className="font-mono font-bold text-stone-900 bg-stone-100 px-2 py-0.5 rounded border border-stone-200">
                            {b.roomId || b.id?.substring(0, 8)}
                          </span>
                        </td>

                        {/* Service */}
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-stone-900">
                            {b.service?.name || "General"}
                          </div>
                          <div className="text-[11px] text-stone-500">
                            {b.subService?.name || "Standard Skill"}
                          </div>
                        </td>

                        {/* User */}
                        <td className="py-3.5 px-4">
                          {b.user?.id ? (
                            <Link
                              to={`/admin/user/${b.user.id}`}
                              className="font-bold text-stone-900 hover:text-amber-700 underline decoration-stone-300 transition-colors block"
                            >
                              {b.user?.name || "Client"}
                            </Link>
                          ) : (
                            <span className="font-bold text-stone-900">
                              {b.user?.name || "Client"}
                            </span>
                          )}
                          <div className="text-[11px] text-stone-400">
                            {b.user?.phone || "No Phone"}
                          </div>
                        </td>

                        {/* Provider */}
                        <td className="py-3.5 px-4">
                          {b.provider?.id ? (
                            <Link
                              to={`/provider/${b.provider.id}/verified`}
                              className="font-bold text-stone-900 hover:text-amber-700 underline decoration-stone-300 transition-colors block"
                            >
                              {b.provider?.name || "Provider"}
                            </Link>
                          ) : (
                            <span className="font-semibold text-stone-500 italic">
                              {b.provider?.name || "Unassigned"}
                            </span>
                          )}
                          <div className="text-[11px] text-stone-400">
                            {b.provider?.phone || "N/A"}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${getStatusBadge(
                              b.status,
                            )}`}
                          >
                            {b.status}
                          </span>
                        </td>

                        {/* Scheduled Slot */}
                        <td className="py-3.5 px-4 text-stone-600">
                          <div className="font-semibold text-stone-900">
                            {b.scheduledDate
                              ? new Date(b.scheduledDate).toLocaleDateString()
                              : "Immediate"}
                          </div>
                          <div className="text-[11px] text-stone-400">
                            {b.scheduledTime || "Time slot unassigned"}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => fetchBookingDetails(b.id)}
                              disabled={actionLoading}
                              className="px-3 py-1 bg-stone-900 hover:bg-stone-800 text-[#F8D346] text-xs font-bold rounded-lg transition-all shadow-xs"
                            >
                              Dossier
                            </button>

                            {(b.status === "PENDING" ||
                              b.status === "ACCEPTED" ||
                              b.status === "ONGOING" ||
                              b.status === "SEARCHING") && (
                              <button
                                onClick={() => {
                                  setSelectedBooking(b);
                                  setShowCancelModal(true);
                                }}
                                disabled={actionLoading}
                                className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold rounded-lg border border-rose-200 transition-all"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Bar */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-stone-100 bg-stone-50/50 flex flex-col sm:flex-row items-center justify-between gap-3">
                <span className="text-xs text-stone-500">
                  Showing {(currentPage - 1) * limit + 1} to{" "}
                  {Math.min(currentPage * limit, totalBookings)} of{" "}
                  {totalBookings} records
                </span>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() =>
                      fetchBookings(Math.max(1, currentPage - 1), statusFilter)
                    }
                    disabled={currentPage === 1 || loading}
                    className="px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-xs font-bold text-stone-700 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    ← Previous
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((p) => {
                        return (
                          p === 1 ||
                          p === totalPages ||
                          Math.abs(p - currentPage) <= 1
                        );
                      })
                      .map((page, idx, arr) => (
                        <React.Fragment key={page}>
                          {idx > 0 && arr[idx - 1] !== page - 1 && (
                            <span className="px-1 text-xs text-stone-400">
                              ...
                            </span>
                          )}
                          <button
                            onClick={() =>
                              fetchBookings(page, statusFilter)
                            }
                            className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                              currentPage === page
                                ? "bg-stone-900 text-[#F8D346] shadow-sm"
                                : "bg-white text-stone-700 border border-stone-200 hover:bg-stone-50"
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                  </div>

                  <button
                    onClick={() =>
                      fetchBookings(
                        Math.min(totalPages, currentPage + 1),
                        statusFilter,
                      )
                    }
                    disabled={currentPage === totalPages || loading}
                    className="px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-xs font-bold text-stone-700 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Details Lightbox Modal */}
      {showDetailsModal && selectedBooking && (
        <div className="fixed inset-0 bg-stone-950/80 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-stone-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-stone-100 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-xs font-bold bg-stone-100 text-stone-800 px-2 py-0.5 rounded border border-stone-200">
                    {selectedBooking.roomId}
                  </span>
                  <span
                    className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold border ${getStatusBadge(
                      selectedBooking.status,
                    )}`}
                  >
                    {selectedBooking.status}
                  </span>
                </div>
                <h3 className="text-lg font-black text-stone-900">
                  {selectedBooking.service?.name} —{" "}
                  {selectedBooking.subService?.name}
                </h3>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center justify-center text-sm font-bold transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Section: OTP & Verification Security */}
              {(() => {
                const isHandshakeDone =
                  selectedBooking.status === "COMPLETED" ||
                  selectedBooking.status === "STARTED" ||
                  selectedBooking.status === "ONGOING" ||
                  Boolean(selectedBooking.timestamps?.startedAt);

                const isCancelled =
                  selectedBooking.status === "CANCELLED" ||
                  selectedBooking.status === "REJECTED";

                return (
                  <div className="bg-[#F8D346]/15 border border-[#F8D346]/40 rounded-2xl p-4 flex items-center justify-between">
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-stone-700 block">
                        Service Handshake OTP
                      </span>
                      <span className="font-mono text-2xl font-black text-stone-900 tracking-wider">
                        {selectedBooking.serviceOtp || "N/A"}
                      </span>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold border ${
                        isHandshakeDone
                          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                          : isCancelled
                            ? "bg-rose-100 text-rose-800 border-rose-200"
                            : "bg-amber-100 text-amber-800 border-amber-200"
                      }`}
                    >
                      {isHandshakeDone
                        ? "✓ Handshake Verified & Consumed"
                        : isCancelled
                          ? "✕ Cancelled Before Handshake"
                          : "⏳ Pending Handshake"}
                    </span>
                  </div>
                );
              })()}

              {/* 4-Column Grid: Scheduling, Pricing, Location, Participants */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Scheduling */}
                <div className="bg-stone-50 rounded-2xl p-4 border border-stone-200/70">
                  <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">
                    Schedule & Timing
                  </h4>
                  <div className="space-y-1.5 text-xs text-stone-700">
                    <div className="flex justify-between">
                      <span className="text-stone-500">Scheduled Date:</span>
                      <span className="font-bold text-stone-900">
                        {selectedBooking.scheduling?.scheduledDate
                          ? new Date(
                              selectedBooking.scheduling.scheduledDate,
                            ).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">Time Slot:</span>
                      <span className="font-bold text-stone-900">
                        {selectedBooking.scheduling?.scheduledTime || "Flexible"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">Expected Duration:</span>
                      <span className="font-bold text-stone-900">
                        {selectedBooking.scheduling?.expectedHours || 2} Hours
                      </span>
                    </div>
                  </div>
                </div>

                {/* Pricing */}
                <div className="bg-stone-50 rounded-2xl p-4 border border-stone-200/70">
                  <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">
                    Billing & Remittance
                  </h4>
                  <div className="space-y-1.5 text-xs text-stone-700">
                    <div className="flex justify-between">
                      <span className="text-stone-500">Gross Invoice:</span>
                      <span className="font-black text-stone-900">
                        ₹{selectedBooking.pricing?.resolvedPrice || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">Provider Payout:</span>
                      <span className="font-black text-emerald-700">
                        ₹{selectedBooking.pricing?.providerEarnings || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">Platform Commission:</span>
                      <span className="font-black text-amber-800">
                        ₹{selectedBooking.pricing?.adminCommission || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="bg-stone-50 rounded-2xl p-4 border border-stone-200/70">
                  <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">
                    Service Address
                  </h4>
                  <p className="text-xs font-semibold text-stone-800 mb-1">
                    {selectedBooking.location?.address || "No Street Address"}
                  </p>
                  <p className="text-xs text-stone-500">
                    {selectedBooking.location?.city || "City"},{" "}
                    {selectedBooking.location?.state || "State"} -{" "}
                    {selectedBooking.location?.pincode || "Pincode"}
                  </p>
                </div>

                {/* Participants */}
                <div className="bg-stone-50 rounded-2xl p-4 border border-stone-200/70">
                  <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">
                    Parties Involved
                  </h4>
                  <div className="space-y-2 text-xs">
                    <div>
                      <span className="text-stone-400 text-[10px] block">
                        Consumer
                      </span>
                      <span className="font-bold text-stone-900">
                        {selectedBooking.user?.name}
                      </span>{" "}
                      <span className="text-stone-500">
                        ({selectedBooking.user?.phone})
                      </span>
                    </div>
                    <div>
                      <span className="text-stone-400 text-[10px] block">
                        Service Provider
                      </span>
                      <span className="font-bold text-stone-900">
                        {selectedBooking.provider?.name || "Unassigned"}
                      </span>{" "}
                      <span className="text-stone-500">
                        ({selectedBooking.provider?.phone || "N/A"})
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {(selectedBooking.notes?.userNotes ||
                selectedBooking.notes?.providerNotes) && (
                <div className="bg-stone-50 rounded-2xl p-4 border border-stone-200/70 space-y-2 text-xs">
                  <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider">
                    Instruction Notes
                  </h4>
                  {selectedBooking.notes?.userNotes && (
                    <p className="text-stone-700">
                      <strong>Client Note:</strong>{" "}
                      {selectedBooking.notes.userNotes}
                    </p>
                  )}
                  {selectedBooking.notes?.providerNotes && (
                    <p className="text-stone-700">
                      <strong>Provider Note:</strong>{" "}
                      {selectedBooking.notes.providerNotes}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {showCancelModal && selectedBooking && (
        <div className="fixed inset-0 bg-stone-950/80 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-stone-200 p-6">
            <div className="flex items-center gap-3 mb-4 text-rose-600">
              <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
                <svg
                  className="w-5 h-5 text-rose-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-black text-stone-900">
                  Cancel Reservation
                </h3>
                <p className="text-xs text-stone-500">
                  Order ID: {selectedBooking.roomId}
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-5">
              <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider">
                Mandatory Cancellation Reason
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-stone-50 focus:bg-white border border-stone-200 rounded-xl text-xs font-semibold text-stone-900 focus:outline-none focus:ring-2 focus:ring-rose-500 resize-none"
                rows={3}
                placeholder="Reason for cancellation (will be visible to client and provider)..."
                required
              />
            </div>

            <div className="flex items-center justify-end gap-2.5">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason("");
                }}
                className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-xs font-bold transition-colors"
                disabled={actionLoading}
              >
                Go Back
              </button>
              <button
                onClick={() => cancelBooking(selectedBooking.id, cancelReason)}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm disabled:opacity-50"
                disabled={actionLoading || !cancelReason.trim()}
              >
                {actionLoading ? "Cancelling..." : "Confirm Cancellation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
